import type { MeetingSort, MeetingStatus, OverviewStatus, Platform } from "./constants";

export type { MeetingSort, MeetingStatus, OverviewStatus, Platform } from "./constants";
export type UserPlan = "free" | "pro";
export type Locale = "en" | "id";
export type BillingMode = "bypass" | "shadow" | "enforced";
export type WorkspaceType = "personal" | "team";
export type WorkspaceRole = "owner" | "admin" | "member" | "billing_admin";

// The profile contract uses completed onboarding steps, with 3 meaning setup
// is complete. Keep the UI aligned with the server's 0..3 range.
export const ONBOARDING_COMPLETE_PROGRESS = 3;

export interface Workspace {
  id: string;
  type: WorkspaceType;
  name: string;
  owner_user_id: string;
  preferences: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  role?: WorkspaceRole;
}

export interface WorkspaceMembership {
  workspace_id: string;
  user_id: string;
  role: WorkspaceRole;
  status: "active" | "revoked";
}

export interface WorkspaceInvitationPreview {
  workspace: {
    id: string;
    type: "team";
    name: string;
    default_visibility: "private" | "workspace";
  };
  inviter: {
    display_name: string;
  };
  role: Exclude<WorkspaceRole, "owner">;
  expires_at: string;
}

export type TodayCategory = "needs_review" | "blocked" | "due" | "recent_verified" | "follow_up";
export type TodayReasonCode = "deadline" | "dependency" | "recency";
export type WorkspaceResourceType = "meeting" | "space" | "commitment" | "activity" | "goal";

export interface TodayItem {
  id: string;
  category: TodayCategory;
  resource_type: WorkspaceResourceType;
  resource_id: string;
  title: string;
  reason_code: TodayReasonCode;
  resource_path: string;
  updated_at: string;
}

export interface TodayResponse {
  workspace_id: string;
  sections: Record<TodayCategory, TodayItem[]>;
}

export type SearchResultKind = "space" | "meeting" | "commitment" | "segment";

export interface SearchResult {
  kind: SearchResultKind;
  id: string;
  title: string;
  snippet?: string;
  resource_path: string;
  matched_segment_id?: string;
  updated_at?: string;
}

export interface SearchResponse {
  workspace_id: string;
  query: string;
  items: SearchResult[];
  next_cursor: string | null;
}

export interface FeatureCapabilities {
  summaries: boolean;
  follow_through: boolean;
  audio: boolean;
  billing_management: boolean;
}

export interface AppConfig {
  contract_version: "v1";
  billing_mode: BillingMode;
  commercial_entitled: boolean;
  would_commercially_allow: boolean;
  monetization_enabled: boolean;
  web_app_url: string;
  app_version: string;
  billing_checkout_url: string;
  billing_portal_url: string;
  safety_limits: SafetyLimits;
}

export interface SafetyLimits {
  max_transcript_chars: number;
  max_segments: number;
  max_audio_session_minutes: number;
  max_agent_mutations: number;
  max_agent_retry_attempts: number;
}

export interface UsageLimits {
  summaries_per_month: number;
}

export interface UserProfile {
  user_id: string;
  email: string;
  plan: UserPlan;
  monetization_enabled: boolean;
  summaries_used_this_month: number;
  limits: UsageLimits | null;
  locale: Locale;
  timezone: string;
  onboarding_progress: number;
  display_name?: string;
  avatar_url?: string;
  /** Whether an email is sent when a meeting overview is ready. Sign-in links are always sent. */
  email_overview_enabled?: boolean;
  billing_mode?: BillingMode;
  commercial_entitled?: boolean;
  would_commercially_allow?: boolean;
  safety_limits?: SafetyLimits;
}

export interface UserPreferences {
  locale: Locale;
  timezone: string;
}

export interface OnboardingProgressResponse {
  locale: Locale;
  timezone: string;
  onboarding_progress: number;
}

export interface TokenResponse {
  access_token: string;
  /** Present for extension/legacy clients; web clients receive an HttpOnly cookie. */
  refresh_token?: string;
}

export interface MagicLinkResponse {
  ok: boolean;
  dev_magic_link?: string;
}

export interface MeetingListItem {
  meeting_id: string;
  title: string;
  platform: Platform;
  status: MeetingStatus;
  started_at: string;
  ended_at: string | null;
  segment_count: number;
  /** True when the viewer may rename or delete the meeting. */
  can_edit: boolean;
  spaces?: Space[];
  search_match?: MeetingSearchMatch;
}

export type SpaceStatus = "active" | "archived";
export interface Space {
  space_id: string;
  workspace_id: string;
  name: string;
  description?: string;
  status: SpaceStatus;
  version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SpaceEvent {
  event_id: string;
  event_type: "created" | "edited" | "archived" | "restored" | "linked" | "unlinked";
  actor_user_id: string;
  created_at: string;
  meeting_id?: string;
  commitment_id?: string;
}

export interface SpaceDashboard {
  space: Space;
  open_commitments: Commitment[];
  recent_meetings: MeetingListItem[];
  activity: SpaceEvent[];
}

export interface MeetingSearchMatch {
  kind: "title" | "space" | "transcript";
  snippet: string;
  count: number;
  segment_id?: string;
  timestamp?: string;
}

export interface MeetingQuery {
  q?: string;
  spaces?: string[];
  statuses?: MeetingStatus[];
  platforms?: Platform[];
  overviews?: OverviewStatus[];
  from?: string;
  to?: string;
  sort?: MeetingSort;
  cursor?: string | null;
  limit?: number;
}

export interface MeetingListPage {
  contract_version: "v1";
  meetings: MeetingListItem[];
  next_cursor: string | null;
  has_more: boolean;
  /** Matches across all pages; only present on the first page. */
  total: number | null;
}

export interface MeetingFacets {
  total: number;
  platform: Record<Platform, number>;
  status: Record<MeetingStatus, number>;
  overview: Record<OverviewStatus, number>;
}

/** Where a transcript line came from. Never shown as a label; "caption" when the server omits it. */
export type TranscriptSource = "caption" | "audio";

/** Whether a line was spoken or typed in the meeting's chat; "transcript" when the server omits it. */
export type TranscriptCategory = "transcript" | "chat";

export interface MeetingTranscript {
  id: string;
  speaker: string;
  text: string;
  timestamp: string;
  /** Meeting time in ms; when absent, derive it from `timestamp`. */
  offset_ms?: number;
  source?: TranscriptSource;
  category?: TranscriptCategory;
  /** When the server last wrote or revised the line; the live poll sends the newest value back as `updated_since`. */
  updated_at?: string;
}

export interface TranscriptPage {
  items: MeetingTranscript[];
  next_cursor: string | null;
  has_more: boolean;
}

/** Why a transcript line matched, strongest first. */
export type TranscriptMatchType = "exact" | "similar" | "sounds";

export interface TranscriptSearchHit {
  segment_id: string;
  speaker: string;
  timestamp: string;
  /** The text the ranges refer to; it can differ from an already loaded page if a caption was revised. */
  text: string;
  match_type: TranscriptMatchType;
  matched_terms: number;
  /** [start, end) offsets in UTF-16 code units of `text`, sorted and non-overlapping. */
  ranges: Array<[number, number]>;
  /** Transcript cursor that starts a page at this segment; null for the first segment. */
  cursor: string | null;
  /** Meeting time in ms; when absent, derive it from `timestamp`. */
  offset_ms?: number;
  source?: TranscriptSource;
}

export interface TranscriptSearchResult {
  query: string;
  items: TranscriptSearchHit[];
  /** How many lines matched; `items` holds at most the requested limit of them. */
  total_matches: number;
  /** True when the meeting is too long to search whole. */
  truncated: boolean;
}

export interface MeetingSummary {
  status?: "queued" | "running" | "ready" | "failed" | "skipped";
  skip_reason?: string;
  summary_id: string;
  text: string;
  action_items: string[];
  decisions: string[];
  generated_at: string;
}

export interface MeetingParticipant {
  name: string;
}

/** The meeting fields returned by detail and by rename. */
export interface MeetingCore {
  meeting_id: string;
  title: string;
  platform: Platform;
  status: MeetingStatus;
  started_at: string;
  ended_at: string | null;
  can_edit: boolean;
}

export interface MeetingDetail extends MeetingCore {
  /** Distinct speakers across the whole transcript, available before any transcript page loads. */
  participants: MeetingParticipant[];
  spaces?: Space[];
  /** Present only when the meeting has at least one audio recording (C2 §1). */
  audio?: MeetingAudioSummary;
}

/** Aggregate over every recording of a meeting. */
export type AudioRecordingStatus = "recording" | "uploading" | "processing" | "ready" | "failed" | "deleted";
export type AudioTranscriptionStatus = "none" | "queued" | "processing" | "ready" | "failed";

export interface AudioUploadProgress {
  uploaded: number;
  expected: number;
}

/** The `audio` block of meeting detail (C2 §1). Offsets are meeting time in ms. */
export interface MeetingAudioSummary {
  recording_status: AudioRecordingStatus;
  transcription_status: AudioTranscriptionStatus;
  /** The server's billing toggle; when false no transcription state or retry is shown. */
  transcription_enabled: boolean;
  upload_progress: AudioUploadProgress | null;
  partial: boolean;
  audio_end_offset_ms: number | null;
  meeting_duration_ms: number;
}

/** One stretch of meeting time that has audio, and where it sits in the joined playback file. */
export interface AudioPiece {
  start_offset_ms: number;
  end_offset_ms: number;
  audio_start_ms: number;
}

/** A presigned playback link. Never stored outside memory and never logged. */
export interface AudioPlayback {
  url: string;
  expires_at: string;
  mime_type: string;
  duration_ms: number;
  version: number;
}

export interface AudioPeaks {
  /** Audio time covered by one value. */
  resolution_ms: number;
  /** One unsigned byte per `resolution_ms`, base64. */
  values_b64: string;
}

/** `GET /meetings/{id}/audio` (C2 §2). */
export interface MeetingAudio {
  recording_status: AudioRecordingStatus;
  transcription_status: AudioTranscriptionStatus;
  transcription_enabled: boolean;
  partial: boolean;
  meeting_duration_ms: number;
  /** Only while `recording_status` is ready. */
  playback: AudioPlayback | null;
  /** Ordered by `start_offset_ms`, non-overlapping. */
  pieces: AudioPiece[];
  peaks: AudioPeaks | null;
}

export interface AudioTranscriptionRetry {
  transcription_status: AudioTranscriptionStatus;
}

export type ReportLanguage = "en" | "id" | "mixed";
export type SummaryTemplate = "general" | "customer_call" | "customer_success";
export interface SummaryOptions {
  output_language: ReportLanguage;
  template: SummaryTemplate;
}
export type ReportStatus = "draft" | "published" | "stale";
export type EvidenceSource = "caption" | "chat" | "audio_transcription";

export interface EvidenceSpan {
  segment_id: string;
  revision: number;
  source?: EvidenceSource;
  quote?: string;
}

export interface ReportAction {
  id: string;
  text: string;
  evidence: EvidenceSpan[];
  confidence?: string;
}

export interface StructuredReport {
  schema_version: "summary.v1";
  language: ReportLanguage;
  summary: string;
  action_items: ReportAction[];
  decisions: ReportAction[];
  open_questions: ReportAction[];
  provenance: EvidenceSpan[];
}

export interface ReportRecord {
  report_id: string;
  meeting_id: string;
  transcript_version_id: string;
  schema_version: "summary.v1";
  language: ReportLanguage;
  status: ReportStatus;
  version: number;
  report: StructuredReport;
  created_at: string;
  updated_at: string;
}

export interface ReportVersionMetadata {
  report_id: string;
  meeting_id: string;
  transcript_version_id: string;
  schema_version: "summary.v1";
  language: ReportLanguage;
  status: ReportStatus;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface ReportVersionList {
  contract_version: "v1";
  meeting_id: string;
  versions: ReportVersionMetadata[];
}

export interface ReportFieldChange {
  path: string;
  before?: unknown;
  after?: unknown;
}

export interface ReportComparison {
  schema_version: "summary.v1";
  meeting_id: string;
  from: ReportRecord;
  to: ReportRecord;
  changes: ReportFieldChange[];
}

export interface Customer {
  customer_id: string;
  workspace_id: string;
  name: string;
  domain?: string;
  created_by: string;
  version?: number;
  status?: "active" | "merged";
  merged_into_customer_id?: string;
  merged_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerHistoryEvent {
  event_id: string;
  customer_id: string;
  meeting_id?: string;
  event_type: "edited" | "associated" | "disassociated" | "merged";
  actor_user_id: string;
  from_customer_id?: string;
  to_customer_id?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  created_at: string;
}

export interface CustomerHistoryResponse {
  contract_version: "v1";
  customer_id: string;
  items: CustomerHistoryEvent[];
}

export interface CustomerMeeting {
  meeting_id: string;
  title: string;
  status: string;
  started_at: string;
  association_type: string;
}

export interface MeetingAssociation {
  meeting_id: string;
  customer_id: string;
  association_type: "suggested" | "confirmed";
  confidence?: number;
  confirmed_at?: string;
}

export interface CommitmentSuggestion {
  suggestion_id: string;
  meeting_id: string;
  customer_id?: string;
  source_report_id?: string;
  source_action_id?: string;
  text: string;
  due_phrase?: string;
  evidence: Array<Record<string, unknown>>;
  status: "suggested" | "confirmed" | "rejected";
  created_at: string;
}

export interface Commitment {
  commitment_id: string;
  meeting_id: string;
  customer_id?: string;
  suggestion_id: string;
  owner_user_id: string;
  text: string;
  due_phrase?: string;
  due_at?: string;
  timezone: string;
  status: "open" | "completed" | "cancelled";
  version?: number;
  external_task_links?: ExternalTaskLink[];
  created_at: string;
  updated_at: string;
  spaces?: Space[];
}

export interface ExternalTaskLink {
  link_id: string;
  provider: string;
  external_task_id: string;
  url?: string;
  created_by: string;
  created_at: string;
}

export interface CommitmentHistoryEvent {
  event_id: string;
  commitment_id: string;
  actor_user_id: string;
  from_status?: "open" | "completed" | "cancelled";
  to_status: "open" | "completed" | "cancelled";
  event_type?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  created_at: string;
}

export interface CustomerMergeConflict {
  type: "external_id_conflict" | "meeting_association_conflict";
  provider?: string;
  external_id?: string;
  meeting_id?: string;
  source_record_id?: string;
  target_record_id?: string;
  detail?: string;
}

export interface CustomerMergeCandidate {
  customer: Customer;
  conflicts: CustomerMergeConflict[];
}

export interface CustomerMergeCandidatesResponse {
  contract_version: "customer-merge-candidates.v1";
  source_customer: Customer;
  items: CustomerMergeCandidate[];
}

export interface CustomerMergePreview {
  contract_version: "customer-merge-preview.v1";
  source_customer: Customer;
  target_customer: Customer;
  conflicts: CustomerMergeConflict[];
  can_confirm: boolean;
}

export interface CustomerMergeResponse {
  contract_version: "customer-merge.v1";
  customer: Customer;
  source_customer_id: string;
  merged_customer_id: string;
}

export interface CommitmentListResponse {
  contract_version: "v1";
  items: Commitment[];
}

export interface CommitmentHistoryResponse {
  contract_version: "v1";
  commitment_id: string;
  items: CommitmentHistoryEvent[];
}

export interface ShareGrant {
  share_id: string;
  meeting_id: string;
  recipient_user_id: string;
  capability: "recap" | "full_transcript" | "audio";
  expires_at?: string;
  revoked_at?: string;
  created_at: string;
}

export interface ShareGrantListResponse {
  contract_version: "v1";
  meeting_id: string;
  items: ShareGrant[];
}

export interface WorkspaceMember {
  user_id: string;
  display_name: string;
  role: "owner" | "admin" | "member" | "billing_admin";
  status: "active";
  joined_at: string;
}

export interface WorkspaceMemberListResponse {
  contract_version: "v1";
  workspace_id: string;
  items: WorkspaceMember[];
}

export type WorkflowState = "preparing" | "needs_information" | "needs_review" | "executing" | "partially_completed" | "completed" | "paused" | "cancelled" | "expired" | "blocked" | "reconciling";
export type WorkflowActionKind = "clarification" | "crm_note" | "crm_task" | "email_draft" | "email_send";
export type WorkflowActionStatus = "pending" | "blocked" | "approved" | "verified" | "failed" | "reconciling" | "manual";
export type WorkflowEffectStatus = "queued" | "running" | "reconciling" | "succeeded" | "failed" | "cancelled";
export type WorkflowReceiptStatus = "verified" | "reconciling" | "failed" | "blocked";

export interface WorkflowReviewSource { version: string; digest: string }
export interface WorkflowPolicyReview { approval_required: boolean; email_send_requires_review: boolean; max_external_mutations: number; max_retryable_attempts: number; approval_expiry_hours: number }
export interface WorkflowActionReview {
  action_id: string;
  step_id: string;
  version: number;
  kind: WorkflowActionKind;
  status: WorkflowActionStatus;
  payload: Record<string, unknown>;
  payload_hash: string;
  source: WorkflowReviewSource;
  requires_approval: boolean;
  created_at: string;
}
export interface WorkflowEffectReview { effect_id: string; action_id: string; status: WorkflowEffectStatus; attempt: number; max_attempts: number; created_at: string; updated_at: string; completed_at?: string }
export interface WorkflowReceiptReview { receipt_id: string; effect_id: string; action_id: string; step_id: string; status: WorkflowReceiptStatus; provider_object_id?: string; response_hash?: string; created_at: string }
export interface WorkflowActivityReview { activity_id: string; activity_type: "transition" | "effect_attempt"; occurred_at: string; transition_id?: string; effect_attempt_id?: string; effect_id?: string; action_id?: string; attempt?: number; status?: string; error_category?: string; from_state?: WorkflowState; to_state?: WorkflowState; expected_state_version?: number }
export interface WorkflowTransitionReview { transition_id: string; from_state: WorkflowState; to_state: WorkflowState; expected_state_version: number; source: WorkflowReviewSource; preparation_digest?: string; idempotency_key: string; created_at: string }
export interface WorkflowReview {
  schema_version: "workflow-review.v1";
  job_id: string;
  workspace_id: string;
  goal_id: string;
  state: WorkflowState;
  state_version: number;
  policy_version: number;
  policy: WorkflowPolicyReview;
  source: WorkflowReviewSource;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  actions: WorkflowActionReview[];
  effects: WorkflowEffectReview[];
  receipts: WorkflowReceiptReview[];
  transitions: WorkflowTransitionReview[];
  activity: WorkflowActivityReview[];
}

export interface WorkflowReceiptCounts { verified: number; reconciling: number; failed: number; blocked: number }
export interface WorkflowRunListItem {
  job_id: string;
  goal_id: string;
  state: WorkflowState;
  state_version: number;
  source: WorkflowReviewSource;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  retry_remaining: number;
  receipt_counts: WorkflowReceiptCounts;
  has_reconciliation: boolean;
}
export interface WorkflowRunListResponse { schema_version: "workflow-runs.v1"; workspace_id: string; items: WorkflowRunListItem[]; next_cursor: string | null }
export interface WorkflowActivityListItem {
  activity_id: string;
  job_id: string;
  activity_type: "transition" | "effect_attempt";
  occurred_at: string;
  effect_id?: string;
  action_id?: string;
  attempt?: number;
  status?: string;
  error_category?: string;
  error_code?: string;
  error_detail?: string;
  from_state?: WorkflowState;
  to_state?: WorkflowState;
  expected_state_version?: number;
}
export interface WorkflowActivityListResponse { schema_version: "workflow-activity.v1"; workspace_id: string; items: WorkflowActivityListItem[]; next_cursor: string | null }
export interface WorkflowRetryItem { action_id: string; effect_id: string; attempt: number; max_attempts: number; remaining_attempts: number; reason: string }
export interface WorkflowRetryResponse { schema_version: "workflow-retry.v1"; workspace_id: string; job_id: string; status: "queued" | "no_retryable_work" | "reconciliation_required"; queued: WorkflowRetryItem[]; reconciliation_required: WorkflowRetryItem[]; retry_remaining: number }
export interface WorkflowReconciliationReceipt { receipt_id: string; status: WorkflowReceiptStatus; provider_object_id?: string; response_hash?: string; error_category?: string; error_code?: string; error_detail?: string; created_at: string; reconciled_at?: string }
export interface WorkflowReconciliationItem { effect_id: string; action_id: string; attempt: number; max_attempts: number; status: "reconciling" | "running"; receipt: WorkflowReconciliationReceipt }
export interface WorkflowReconciliationResponse { schema_version: "workflow-reconciliation.v1"; workspace_id: string; job_id: string; status: "clear" | "reconciling"; items: WorkflowReconciliationItem[] }

export interface WorkspacePolicy { contract_version: "workspace-policy.v1"; workspace_id: string; version: number; preferences: { default_visibility: "private" | "workspace" }; autonomy: { crm_notes_and_tasks: boolean; email_send: "review_required" }; updated_at: string }
export type UsageCapability = "summary" | "transcription" | "workflow";
export interface UsageBudget { contract_version: "v1"; workspace_id: string; capability: UsageCapability; period_start: string; period_end: string; limit_units: number; used_units: number; reserved_units: number; remaining_units: number; status: "active" | "paused" }
export interface BillingAction { contract_version: "v1"; workspace_id: string; action: "checkout" | "portal"; action_state: "no_op" | "redirect_ready" | "unavailable"; reason_code?: "billing_mode_bypass" | "billing_mode_shadow" | "no_subscription" | "action_unconfigured"; billing_mode: BillingMode; state_code: BillingState | "bypass"; commercial_entitled: boolean; redirect_url?: string }
export interface WorkflowEvidenceRef { ref_type: "meeting" | "transcript_segment" | "report" | "customer" | "commitment"; ref_id: string; revision?: number; label?: string; quote?: string }
export interface WorkflowGoalStepRequest { step_id: string; kind: WorkflowActionKind; selected?: boolean; payload: Record<string, unknown>; reason: string; evidence_refs: WorkflowEvidenceRef[] }
export interface WorkflowGoalCreateRequest { schema_version: "workflow-goal.v1"; goal_id?: string; title: string; objective: string; source: WorkflowReviewSource; evidence_refs: WorkflowEvidenceRef[]; steps: WorkflowGoalStepRequest[]; idempotency_key: string }
export interface WorkflowGoalStepReview { action_id?: string; step_id: string; version?: number; kind: WorkflowActionKind; status: string; review_state: string; selected: boolean; payload: Record<string, unknown>; payload_hash?: string; source: WorkflowReviewSource; reason: string; evidence_refs: WorkflowEvidenceRef[] }
export interface WorkflowGoalReview { schema_version: "workflow-goal.v1"; goal_id: string; workspace_id: string; job_id: string; version: number; title: string; objective: string; state: WorkflowState; state_version: number; policy_version: number; source: WorkflowReviewSource; evidence_refs: WorkflowEvidenceRef[]; steps: WorkflowGoalStepReview[] }
export interface WorkflowActionMutationRequest { schema_version: "workflow-action-review.v1"; operation: "draft" | "edit" | "reject"; expected_version: number; selected?: boolean; kind?: WorkflowActionKind; payload?: Record<string, unknown>; reason?: string; evidence_refs?: WorkflowEvidenceRef[]; source: WorkflowReviewSource; policy_version: number }
export interface WorkflowActionMutationResult { schema_version: "workflow-action-review.v1"; action_id: string; step_id: string; version: number; status: string; review_state: string; selected: boolean; kind: WorkflowActionKind; payload: Record<string, unknown>; payload_hash: string; source: WorkflowReviewSource; reason: string; evidence_refs: WorkflowEvidenceRef[] }
export type BillingState = "no_subscription" | "trialing" | "active" | "grace" | "payment_failed" | "cancellation_scheduled" | "paused" | "unpaid" | "cancelled" | "expired" | "incomplete" | "incomplete_expired";
export interface BillingSubscription {
  plan: string;
  status: string;
  currency: string;
  amount_minor: number;
  billing_interval: string;
  seat_count: number;
  pooled_allowance: number;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  cancelled_at?: string;
  grace_until?: string;
  payment_failed_at?: string;
  payment_failure_count: number;
}
export interface BillingStatus {
  contract_version: "v1";
  workspace_id: string;
  billing_mode: BillingMode;
  state_code: BillingState;
  commercial_entitled: boolean;
  subscription: BillingSubscription | null;
}

export interface SharedRecap {
  schema_version: "shared-recap.v1";
  meeting_id: string;
  title: string;
  platform: string;
  report_id: string;
  version: number;
  report: Record<string, unknown>;
}

export interface OkResponse { ok: true }

export type ConnectorProvider = "gmail" | "hubspot";
export type ConnectorStatus = "active" | "revoked" | "expired" | "reconciling";
export type ConnectorHealthStatus = "unknown" | "healthy" | "unhealthy" | "reconciling";

export interface Connector {
  id: string;
  workspace_id: string;
  owner_user_id: string;
  provider: ConnectorProvider;
  external_account_id: string;
  account_type: string;
  api_version: string;
  scopes: string[];
  status: ConnectorStatus;
  health_status: ConnectorHealthStatus;
  created_at: string;
  updated_at: string;
  last_health_at?: string;
  revoked_at?: string;
}

export interface ConnectorListResponse {
  contract_version: "connectors.v1";
  workspace_id: string;
  connectors: Connector[];
}

export interface ConnectorInitiation {
  contract_version: "connectors.v1";
  provider: ConnectorProvider;
  workspace_id: string;
  authorization_url: string;
  expires_at: string;
}

export interface ConnectorHealthResponse {
  contract_version: "connectors.v1";
  health: { status: ConnectorHealthStatus; checked_at: string; provider_code?: string };
}

export interface PrivacyPreferences {
  ai_processing_enabled: boolean;
  retention_days: number;
  updated_at: string;
}

export interface PrivacyResponse {
  contract_version: "v1";
  preferences: PrivacyPreferences;
}

export interface DeletionResult {
  status: "completed";
  requested_at: string;
  content_deleted_at: string;
  cleanup_tasks_queued: number;
  pending_cleanup_tasks: number;
  scope: string[];
  external_copy_status: "not_tracked_by_memoru" | "reconciliation_required";
  backup_expiry: string;
  device_purge_status: "best_effort_device_purge" | "not_requested";
}

export interface SampleWorkflow {
  contract_version: "v1";
  synthetic: true;
  workflow_id: string;
  meeting: { id: string; title: string; platform: string; status: string; started_at: string };
  evidence: Array<{ id: string; speaker: string; text: string; timestamp: string; source: string }>;
  report: {
    schema_version: "summary.v1";
    language: string;
    summary: string;
    decisions: Array<{ id: string; text: string; evidence: string[] }>;
    commitments: Array<{ id: string; text: string; evidence: string[] }>;
  };
  follow_up: { status: string; draft: string; sent: false };
}

export type ExtensionPairingStatus = "not_connected" | "pending" | "connected" | "expired";
export type ExtensionPairingRecovery = "none" | "reconnect" | "expired";
export type ExtensionCaptureRecovery = "none" | "resume_or_restart";

export interface ExtensionHealth {
  contract_version: "v1";
  pairing: { status: ExtensionPairingStatus; recovery_state: ExtensionPairingRecovery; expires_at?: string };
  capture: { status: string; recovery_state: ExtensionCaptureRecovery; updated_at?: string };
}

export interface SummarizeResponse {
  summary_id: string;
  summary: string;
  action_items: string[];
  decisions: string[];
  usage?: {
    summaries_used_this_month: number;
    limit: number | null;
  };
}

export interface AsyncSummaryResult {
  summary_id: string;
  format: "text/markdown";
  url: string;
  url_expires_at: string;
  sha256: string;
  size_bytes: number;
  generated_at: string;
}

export interface AsyncSummaryState {
  meeting_id: string;
  job_id: string;
  status: "queued" | "running" | "done" | "failed";
  output_language?: ReportLanguage;
  template?: SummaryTemplate;
  attempt?: number;
  poll_after_seconds?: number;
  error_code?: string;
  error_message?: string;
  can_retry?: boolean;
  result?: AsyncSummaryResult;
}

export interface ApiErrorBody {
  error?: string;
  code?: string;
  category?: "commercial" | "authorization" | "safety" | "quota" | "provider";
  limit?: number;
  used?: number;
  resets_at?: string;
  /** Client-actionable values on 4xx errors, such as `retry_at` or `retry_after_seconds`. */
  details?: Record<string, unknown>;
}

export interface ConnectExtensionResponse {
  connect_code: string;
  expires_in: number;
}

export type SessionClient = "web" | "extension" | "legacy";

export interface AuthSession {
  id: string;
  client: SessionClient;
  user_agent?: string;
  ip_address?: string;
  created_at: string;
  last_seen_at?: string;
  expires_at: string;
  current: boolean;
}

export interface RevokeSessionsResponse {
  revoked_sessions: number;
}
